import type { IAdTagCreative } from '@feathr/blackbox';
import { CreativeClass, ValidDimensions } from '@feathr/blackbox';
import { DEFAULT_AD_TAG_HTML } from '@feathr/extender/components/creatives/display.utils';

interface IAdTagCreativeParams {
  /** Campaign ID to associate the creative with. If omitted, creative is created at the event level. */
  campaignId?: string;
  /** Event ID to associate the creative with when no campaign is provided. */
  eventId?: string;
  /** The account ID. */
  accountId: string;
  /** Dimension spec name (e.g., 'leaderboard'). */
  spec: string;
  /** Width in pixels. */
  width: number;
  /** Height in pixels. */
  height: number;
}

/**
 * Builds attributes for a new AdTagCreative with the given dimensions.
 *
 * @param params - The parameters for creating the ad tag creative attributes.
 * @returns Partial attributes for creating an AdTagCreative.
 */
export function getAdTagCreativeAttributes(params: IAdTagCreativeParams): Partial<IAdTagCreative> {
  const { campaignId, eventId, accountId, spec, width, height } = params;
  return {
    _cls: CreativeClass.DisplayAdTag,
    adtag: DEFAULT_AD_TAG_HTML,
    mimetype: 'text/html',
    width,
    height,
    spec,
    ...(campaignId ? { parent: campaignId } : {}),
    ...(eventId && !campaignId ? { event: eventId } : {}),
    account: accountId,
    is_enabled: true,
    name: '',
  };
}

/**
 * Finds a dimension definition by its spec name.
 * ValidDimensions is keyed by size string (e.g., '728x90'), so we
 * need to search entries to find one matching a given spec (e.g., 'leaderboard').
 *
 * @param spec - The spec name to look up (e.g., 'leaderboard').
 * @returns The width and height, or undefined if not found.
 */
export function findDimensionBySpec(spec: string): { width: number; height: number } | undefined {
  const entry = Object.values(ValidDimensions).find((dim) => dim.spec === spec);
  if (!entry) {
    return undefined;
  }
  return { width: entry.width, height: entry.height };
}
